import { DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent, Layout, Tab } from "@mcleod/components";
import { Api, ObjectUtil, StringUtil, UserType } from "@mcleod/core";
import { UserLayoutProvider } from "../UserLayoutProvider";
import { AutogenLayoutUserList } from "./autogen/AutogenLayoutUserList";
import { UserListDrilldownHandler } from "./UserListDrilldownHandler";
import { UserTableLayout } from "./UserTableLayout";

export interface UserTabConfig {
    tab: Tab;
    layout: UserTableLayout;
    getUserCount?: () => number;
    onMount?: () => void;
    baseHeaderText?: string;
}

export class UserList extends AutogenLayoutUserList {
    public static refreshOnCrudClose: boolean;
    private userTypeCountMap: Map<UserType, number> = new Map();
    public userTabConfigMap: Map<UserType, UserTabConfig>;

    override async onLoad() {
        await this.configureTabs();
    }

    override async onDrilldown(keyValues: string[]) {
        await new UserListDrilldownHandler(keyValues, this).execute();
    }

    getRouterProps() {
        return { backgroundColor: "backgroundSubtle" };
    }

    private async configureTabs() {
        this.createUIMaps();
        await this.populateUserTypeCountMap();

        for (const userTabConfig of this.userTabConfigMap.values()) {
            this.configureTab(userTabConfig);
            userTabConfig?.layout?.tableUsers?.addAfterCrudCloseListener(() => this.tableUsersAfterCrudClose(userTabConfig));
        }
    }

    private createUIMaps() {
        this.userTabConfigMap = new Map<UserType, UserTabConfig>();

        const isValidUserTableLayout = (component: any): component is UserTableLayout => {
            return component instanceof Layout &&
                "getUserType" in component &&
                typeof component.getUserType === "function" &&
                // handles the case where the layout is permed off (not the nested layout component)
                // and the user dosen't have access. Ideally, the tab containing the nested layout
                // should have been permed off instead.
                StringUtil.isEmptyString(component.loadError) &&
                "tableUsers" in component &&
                component.tableUsers != null;
        };



        this.tabsetUsers.tabs.forEach(tab => {
            const layout = tab.getRecursiveChildren().find(isValidUserTableLayout);
            if (layout) {
                this.userTabConfigMap.set(layout.getUserType(), { tab, layout });
            } else {
                this.tabsetUsers.remove(tab);
            }
        });
    }

    private async populateUserTypeCountMap() {
        const result = await Api.search("common/user-type-counts");
        result?.data?.forEach((row) => {
            const userType = ObjectUtil.getEnumValue(UserType, row.group_value);
            if (userType) {
                this.userTypeCountMap.set(userType, row.count);
            }
        });
    }

    private configureTab(userTabConfig: UserTabConfig) {
        const { tab, layout } = userTabConfig;
        tab.paddingLeft = 12;
        // aligns the left position of the action buttons on each tab regardless of the number of users in each tab
        layout.tableUsers.headingPanel?.labelRowCount?.setProps({ minWidth: 120 });

        userTabConfig.baseHeaderText ??= tab.headingLabel.text;
        this.updateTabCountLabel(userTabConfig);


        userTabConfig.onMount = () => layout.getSourceUsers().executeValidatedSearch();
        tab.addMountListener(userTabConfig.onMount);

        layout.getSourceUsers().addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            if (event.getAction() === DataSourceAction.SEARCH) {
                tab.removeMountListener(userTabConfig.onMount);
                userTabConfig.onMount = null;
            }
        })

        layout.getSourceUsers().addDisplayListener((event: DataDisplayEvent) => {
            if (event.rowData != null) {
                userTabConfig.getUserCount = () => layout.getSourceUsers().actualRowCount;
                this.updateTabCountLabel(userTabConfig);
            }
        });
    }

    onTabMount = (userTabConfig: UserTabConfig) => {
        this.searchLayout(userTabConfig);
    }

    searchLayout(userTabConfig: UserTabConfig) {
        const layout = userTabConfig.layout;
        // When this screen is being opened normally, we want to autosearch, but only for active users.
        // Since a normal DataSource autosearch doesn't include any criteria, this autosearch has to be done here.
        layout.getSourceUsers().executeValidatedSearch();
    }

    private updateTabCountLabel(userTabConfig: UserTabConfig) {
        const userCount = userTabConfig.getUserCount?.()
            ?? this.userTypeCountMap.get(userTabConfig.layout.getUserType())
            ?? 0;

        userTabConfig.tab.headingLabel.text = `${userTabConfig.baseHeaderText} (${userCount})`;
    }

    private tableUsersAfterCrudClose(userTabConfig: UserTabConfig) {
        try {
            if (UserList.refreshOnCrudClose === true) {
                UserLayoutProvider.refreshSettings(this);
            }
        } finally {
            UserList.refreshOnCrudClose = false;
            this.updateTabCountLabel(userTabConfig);
        }
    }
}
